import PropTypes from "prop-types";
import React, { createContext, useContext, useEffect, useRef, useState } from "react";

import { AuthContext } from "@swa-ui/authentication";
import { useDeviceInfo, XLARGE } from "@swa-ui/browser";
import {
  BackgroundVeil,
  Button,
  Caption,
  Dialog,
  Heading,
  Icon,
  Image,
  Link,
  PageGrid,
  SlideIntoView,
  useDialog,
} from "@swa-ui/core";
import { VacationsTheme } from "@swa-ui/design-tokens";
import i18n from "@swa-ui/locale";
import { cookie } from "@swa-ui/persistence";
import { PersonasContext } from "@swa-ui/personas";
import { PlacementFactory, usePlacement } from "@swa-ui/placements";
import { classNames } from "@swa-ui/string";

import { NotificationCenter } from "../../NotificationCenter";
import { Copyright } from "./Copyright";
import styles from "./DotcomHeaderMobile.module.scss";
import { DotcomHeaderMobileLoginPrompt } from "./DotcomHeaderMobileLoginPrompt";
import { OverlayMenus } from "./OverlayMenus";

const COOKIE_DURATION_IN_SECONDS = 1209600;
const MENU_CLOSED = "menuClosed";
const MENU_OPEN = "menuOpen";
const MENU_SELECTION_NONE = "";
const MENU_SLIDE_CLOSED = "slide";
const NOTIFICATION_CENTER_CLOSED = "notifcationCenterClosed";
const NOTIFICATION_CENTER_OPEN = "notiificationCenterOpen";

/**
 * DotcomHeaderMobile provides the header for smaller devices / screens.
 */
export const DotcomHeaderMobileContext = createContext();
export const DotcomHeaderMobile = (props) => {
  const {
    control,
    headerControlPortalId,
    newNotificationCount,
    notificationCenterIconName,
    onViewNotifications,
    pageTitle,
    shouldShowSystemMessagesInNavigation,
    showLoginBanner,
  } = props;
  const { placement: navBottom } = usePlacement("navBottom");
  const { placement: navLogo } = usePlacement("navLogo");
  const { placement: navTop } = usePlacement("navTop");
  const { handleLogout, isLoggedIn } = useContext(AuthContext);
  const [menuRevealed, setMenuRevealed] = useState(MENU_CLOSED);
  const [menuSelection, setMenuSelection] = useState("");
  const [notificationCenterRevealed, setNotificationCenterRevealed] = useState(
    NOTIFICATION_CENTER_CLOSED
  );
  const [notificationCenterIconBadge, setNotificationCenterIconBadge] =
    useState(newNotificationCount);
  const [displayedPlacementId, setDisplayedPlacementId] = useState("");
  const [shouldShowLoginPrompt, setShouldShowLoginPrompt] = useState(
    cookie.getCookie("show_login_banner") !== "false"
  );
  const { hideDialog, isDialogVisible, showDialog } = useDialog("center", "hidden");
  const { isSecureWebView, screenSize } = useDeviceInfo() ?? {};
  const { isVacationsPersona } = useContext(PersonasContext);
  const controlRef = useRef(null);
  const isUserLoggedIn = isLoggedIn();
  const isNotificationCenterOpen = notificationCenterRevealed === NOTIFICATION_CENTER_OPEN;

  useEffect(() => {
    if (shouldShowLoginPrompt) {
      cookie.setCookie("show_login_banner", "true");
    } else {
      cookie.setCookie("show_login_banner", "false", { maxAge: COOKIE_DURATION_IN_SECONDS });
    }
  }, [shouldShowLoginPrompt]);

  useEffect(() => {
    if (isUserLoggedIn) {
      setShouldShowLoginPrompt(false);
    }
  }, [isUserLoggedIn]);

  useEffect(() => {
    setMenuSelection(getActiveMenu());
  }, [navTop]);

  useEffect(() => {
    if (displayedPlacementId) {
      showDialog();
    } else {
      hideDialog();
    }
  }, [displayedPlacementId]);

  return (
    <DotcomHeaderMobileContext.Provider
      value={{ closeHeaderMenu: handleCloseMenu, displayPlacement }}
    >
      <BackgroundVeil {...getVeilProps()} />
      {isDialogVisible && (
        <Dialog {...getDialogProps()}>
          <PlacementFactory id={displayedPlacementId} />
        </Dialog>
      )}
      <PageGrid className={getLogoSectionClass()} maxWidth={screenSize !== XLARGE}>
        <div
          className={classNames(styles.logoContainer, styles.container, {
            [styles.centeredLogo]: isSecureWebView,
          })}
        >
          {renderLogoSection()}
        </div>
      </PageGrid>
      {renderLoginBanner()}
      <PageGrid
        className={classNames(styles.titleLayout, styles.titleSection)}
        maxWidth={screenSize !== XLARGE}
      >
        <div className={classNames(styles.title)}>
          {renderOptionsSection()}
          <SlideIntoView {...getSlideProps()}>{renderOverlay()}</SlideIntoView>
        </div>
      </PageGrid>
    </DotcomHeaderMobileContext.Provider>
  );

  function renderLoginBanner() {
    const shouldShowLoginBanner = showLoginBanner && shouldShowLoginPrompt && !isUserLoggedIn;

    return (
      shouldShowLoginBanner && (
        <DotcomHeaderMobileLoginPrompt
          loginButtonRef={controlRef}
          onClose={() => setShouldShowLoginPrompt(false)}
        />
      )
    );
  }

  function renderLogoSection() {
    return (
      <>
        {!isSecureWebView && (
          <span className={getMenuContainerClass()}>
            <Button {...getMenuProps()}>
              <Icon color="primary-header-search-open" name="Menu" />
            </Button>
          </span>
        )}
        {renderLogo()}
        {!isSecureWebView && (
          <span className={styles.controlContainer}>
            <span ref={controlRef}>{control}</span>
          </span>
        )}
        {shouldShowSystemMessagesInNavigation && renderNotificationCenterItem()}
      </>
    );
  }

  function renderNotificationCenterItem() {
    return (
      <span className={styles.notificationCenterContainer}>
        <Caption {...getNotificationCenterCaptionProps()}>
          <Link {...getNotificationCenterLinkProps()}>
            <Icon
              badge={notificationCenterIconBadge}
              color="primary-header-search-open"
              name={notificationCenterIconName}
            />
          </Link>
        </Caption>
      </span>
    );
  }

  function renderNotificationCenterContent() {
    return (
      <div className={styles.notificationCenterContent}>
        <Heading className={styles.notificationCenterTitle} headingLevel={2}>
          Notifications
        </Heading>
        <NotificationCenter />
      </div>
    );
  }

  function renderOptionsSection() {
    return (
      !!pageTitle && (
        <div className={styles.container}>
          <Heading light styleLevel={4}>
            {pageTitle}
          </Heading>
        </div>
      )
    );
  }

  function renderOverlay() {
    const navBottomContentMenus = navBottom?.placement?.navDrawer?.navDrawerContent;
    const navTopContentMenus = navTop?.placement?.navDrawer?.navDrawerContent;
    const ThemeWrapper = isVacationsPersona ? VacationsTheme : React.Fragment;

    return menuRevealed ? (
      <ThemeWrapper>
        <div className={styles.overlay}>
          <div id={headerControlPortalId} />
          {navTopContentMenus && (
            <OverlayMenus
              closeMenu={handleCloseMenu}
              menus={navTopContentMenus}
              onChangeRevealedMenu={handleChangeRevealedMenu}
              revealedTitle={menuSelection}
            />
          )}
          <div className={styles.placements}>
            <PlacementFactory id="navPromo1" />
          </div>
          {navBottomContentMenus && <OverlayMenus menus={navBottomContentMenus} />}
          {isUserLoggedIn && (
            <Link className={styles.logout} emphasis={false} onClick={handleLogoutClick}>
              {i18n("DotcomHeaderMobile__LOGOUT")}
            </Link>
          )}
          <Copyright />
        </div>
      </ThemeWrapper>
    ) : null;
  }

  function getMenuProps() {
    return {
      className: styles.menu,
      onClick: handleMenuClick,
      styleType: "no-style",
    };
  }

  function getNotificationCenterCaptionProps() {
    return {
      adjoiningContent: renderNotificationCenterContent(),
      location: isNotificationCenterOpen ? "full-screen" : "hidden",
      onClose: () => setNotificationCenterRevealed(NOTIFICATION_CENTER_CLOSED),
      showClose: true,
    };
  }

  function getNotificationCenterLinkProps() {
    return {
      className: styles.menu,
      onClick: handleNotificationCenterClick,
      styleType: "no-style",
    };
  }

  function getDialogProps() {
    return {
      buttonGroupProps: {
        "aria-label-next": i18n("DotcomHeaderMobile__BUTTON_GROUP_ARIA_NEXT"),
        "aria-label-previous": i18n("DotcomHeaderMobile__BUTTON_GROUP_ARIA_PREVIOUS"),
      },
      onClose: handleClose,
      revealed: isDialogVisible,
      showClose: true,
    };
  }

  function getMenuContainerClass() {
    return classNames({
      [styles.menuContainer]: true,
      [styles.menuContainerWithNotificationCenter]: shouldShowSystemMessagesInNavigation,
    });
  }

  function getLogoSectionClass() {
    return classNames(styles.logoLayout, styles.logoSection, {
      [styles.logoSectionRaised]: isNotificationCenterOpen,
    });
  }

  function handleClose() {
    setDisplayedPlacementId("");
  }

  function renderLogo() {
    return (
      <Button {...getLogoButtonProps()}>
        <Image {...getLogoImageProps()} />
      </Button>
    );
  }

  function getLogoButtonProps() {
    const { altText, href, url } = navLogo?.placement || {};

    return {
      "aria-label": altText,
      className: styles.logo,
      href: isSecureWebView ? null : href ?? url ?? "/",
      styleType: "no-style",
    };
  }

  function getLogoImageProps() {
    const { altText: alt, content, height, src, width } = navLogo?.placement || {};

    return {
      alt,
      content,
      height,
      role: "img",
      src,
      width,
    };
  }

  function getVeilProps() {
    return {
      on: menuRevealed === MENU_OPEN,
      onClick: handleVeilClick,
    };
  }

  function getSlideProps() {
    return {
      className: styles.slider,
      onTransformationEnd: menuRevealed === MENU_SLIDE_CLOSED ? handleMenuSlideComplete : undefined,
      slideDirection:
        menuRevealed === MENU_CLOSED
          ? "hidden"
          : menuRevealed === MENU_OPEN
          ? "slideRight"
          : "hideLeft",
    };
  }

  function handleMenuClick() {
    setMenuRevealed(menuRevealed === MENU_CLOSED ? MENU_OPEN : MENU_SLIDE_CLOSED);
  }

  function handleNotificationCenterClick() {
    setNotificationCenterRevealed(
      notificationCenterRevealed === NOTIFICATION_CENTER_CLOSED
        ? NOTIFICATION_CENTER_OPEN
        : NOTIFICATION_CENTER_CLOSED
    );

    if (typeof onViewNotifications === "function") {
      onViewNotifications();
    }

    setNotificationCenterIconBadge(0);
  }

  function handleVeilClick() {
    handleCloseMenu();
  }

  function handleCloseMenu() {
    setMenuRevealed(MENU_SLIDE_CLOSED);
  }

  function displayPlacement(placementId) {
    handleCloseMenu();
    setDisplayedPlacementId(placementId);
  }

  function handleMenuSlideComplete() {
    setMenuRevealed(MENU_CLOSED);
  }

  function handleChangeRevealedMenu(selectedTitle) {
    setMenuSelection(menuSelection === selectedTitle ? MENU_SELECTION_NONE : selectedTitle);
  }

  async function handleLogoutClick() {
    await handleLogout();
    handleCloseMenu();
  }

  function getActiveMenu() {
    const globalNavContent = navTop?.placement?.navDrawer?.navDrawerContent;
    let selectedMenu = MENU_SELECTION_NONE;

    if (globalNavContent) {
      globalNavContent.forEach((menu) => {
        const children = menu.content ?? menu.children;

        children.forEach((menuContent) => {
          if (menuContent.isAutoExpanded) {
            selectedMenu = menuContent.title;
          }
        });
      });
    }

    return selectedMenu;
  }
};

DotcomHeaderMobileContext.displayName = "DotcomHeaderMobileContext";

DotcomHeaderMobile.displayName = "DotcomHeaderMobile";
DotcomHeaderMobile.propTypes = {
  /**
   * Additional classes for positioning the component. Given classes may only position this component for layout
   * purposes, and cannot change how the component renders in any way.
   */
  className: PropTypes.string,

  /**
   * Header control component used to control items like login and locale switching
   */
  control: PropTypes.node,

  /* Element ID to used to display customer information that isn't located directly in the header */
  headerControlPortalId: PropTypes.string,

  /** The name of the icon for the notification center */
  notifcationCenterIconName: PropTypes.string,

  /** A callback to use when the notification center is opened */
  onViewNotifications: PropTypes.func,

  /** Page's title */
  pageTitle: PropTypes.string,

  /** Indicates whether to show the login banner */
  showLoginBanner: PropTypes.bool,

  /** The name of the icon to display for system messages */
  systemMessagesIconName: PropTypes.string,
};
